import { Cloud, LoaderCircle, LockKeyhole, Trash2, UserRound } from 'lucide-react';
import { useState } from 'react';
import { formatBytes } from '@/content/copy';
import type { AccountPort, AccountSnapshot } from '@/ports/account';
import { Modal } from '../Modal';
import { SegmentedControl } from '../SegmentedControl';

export function AccountDialog({ account, snapshot, onClose, onAuthenticated }: { readonly account: AccountPort; readonly snapshot: AccountSnapshot; readonly onClose: () => void; readonly onAuthenticated?: () => Promise<void> | void }) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deletionMessage, setDeletionMessage] = useState<string | null>(null);
  const submit = async () => {
    setLocalError(null);
    try {
      if (mode === 'login') await account.login(email, password);
      else await account.register(email, password);
      if (account.getSnapshot().status === 'authenticated') await onAuthenticated?.();
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : 'No se pudo iniciar sesión.');
    }
  };

  const deleteAccount = async () => {
    setLocalError(null);
    try {
      const result = await account.deleteAccount(deletePassword);
      setDeletionMessage(result.storageCleanupPending
        ? 'La cuenta fue eliminada. Los archivos remotos pendientes se volverán a eliminar automáticamente con las próximas conexiones. Tus borradores locales siguen en este dispositivo.'
        : 'La cuenta y sus datos remotos fueron eliminados. Tus borradores locales siguen en este dispositivo.');
      setDeleteOpen(false);
      setDeletePassword('');
      setDeleteConfirmed(false);
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : 'No se pudo eliminar la cuenta.');
    }
  };

  if (snapshot.status === 'authenticated' || (snapshot.status === 'loading' && deleteOpen)) {
    return (
      <Modal title="Tu cuenta" onClose={onClose} footer={<button className="primary-action" type="button" onClick={onClose}>Listo</button>}>
        <div className="account-authenticated"><UserRound /><div><strong>{snapshot.email}</strong><span>Sesión activa</span></div></div>
        <QuotaSummary snapshot={snapshot} />
        <button className="secondary-action" type="button" onClick={() => void account.logout()}>Cerrar sesión</button>
        <button className="danger-text-action" type="button" aria-expanded={deleteOpen} onClick={() => setDeleteOpen((value) => !value)}><Trash2 /> Eliminar cuenta y datos de la nube</button>
        {deleteOpen ? (
          <div className="danger-confirmation">
            <strong>Esta acción elimina la cuenta y los datos guardados en la nube.</strong>
            <p>Los borradores locales de este dispositivo no se borran.</p>
            <label>Confirmá tu contraseña<input type="password" autoComplete="current-password" value={deletePassword} onChange={(event) => setDeletePassword(event.target.value)} /></label>
            <label className="checkbox-line"><input type="checkbox" checked={deleteConfirmed} onChange={(event) => setDeleteConfirmed(event.target.checked)} /> Entiendo que la cuenta remota no se puede recuperar.</label>
            {snapshot.error || localError ? <p className="inline-error" role="alert">{snapshot.error ?? localError}</p> : null}
            <button className="danger-action" type="button" disabled={!deleteConfirmed || !deletePassword || snapshot.status === 'loading'} onClick={() => void deleteAccount()}>{snapshot.status === 'loading' ? <LoaderCircle className="spin" /> : <Trash2 />} Eliminar definitivamente</button>
          </div>
        ) : null}
      </Modal>
    );
  }

  return (
    <Modal title="Guardar y recuperar proyectos" onClose={onClose} footer={<button className="primary-action" type="button" disabled={snapshot.status === 'loading'} onClick={() => void submit()}>{snapshot.status === 'loading' ? <LoaderCircle className="spin" /> : <Cloud />} {mode === 'login' ? 'Iniciar sesión' : 'Crear cuenta'}</button>}>
      <div className="account-benefit"><LockKeyhole /><div><strong>Tu diseño invitado sigue guardado</strong><span>La cuenta se usa para nube, sincronización y recuperación. No reemplaza ni borra el borrador local.</span></div></div>
      {deletionMessage ? <p className="success-message" role="status">{deletionMessage}</p> : null}
      <SegmentedControl label="Acceso a cuenta" value={mode} options={[{ value: 'login', label: 'Iniciar sesión' }, { value: 'register', label: 'Crear cuenta' }]} onChange={(value) => { setMode(value); account.clearError(); }} />
      <div className="form-stack">
        <label>Email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <label>Contraseña<input type="password" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} value={password} onChange={(event) => setPassword(event.target.value)} /></label>
      </div>
      {snapshot.error || localError ? <p className="inline-error" role="alert">{snapshot.error ?? localError}</p> : null}
      <QuotaSummary snapshot={snapshot} />
    </Modal>
  );
}

function QuotaSummary({ snapshot }: { readonly snapshot: AccountSnapshot }) {
  const authenticated = snapshot.status === 'authenticated' || snapshot.status === 'loading';
  return (
    <dl className="quota-summary">
      <div><dt>{authenticated ? 'Proyectos en nube' : 'Borradores locales'}</dt><dd>{snapshot.quota.projectsUsed}/{snapshot.quota.projectLimit}</dd></div>
      <div><dt>Archivos en este dispositivo</dt><dd>{formatBytes(snapshot.quota.storageUsedBytes)}</dd></div>
      {authenticated ? <div><dt>Límite de nube</dt><dd>{formatBytes(snapshot.quota.storageLimitBytes)} · uso remoto no informado</dd></div> : null}
      <div><dt>Por archivo</dt><dd>{formatBytes(snapshot.quota.assetLimitBytes)}</dd></div>
      <div><dt>PDF</dt><dd>Hasta {snapshot.quota.pdfPageLimit} páginas</dd></div>
    </dl>
  );
}
